"use client";

import React from "react";
import { ArrowRight, CircleCheck, FileText, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
const heroCopy = {
  categoryBadge: {
    en: "Technical Resources & Datasheets",
    zh: "技术资源与工程数据表",
    es: "Recursos técnicos y fichas",
    de: "Technische Ressourcen & Datenblätter",
    fr: "Ressources techniques & fiches",
    pt: "Recursos técnicos e fichas"
  },
  standardBadge: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  h1: {
    en: "Engineering datasheets, CAD files, and compliance guides",
    zh: "工程数据表、CAD 文件与合规标准指南",
    es: "Fichas técnicas de ingeniería, archivos CAD y guías de cumplimiento",
    de: "Technische Datenblätter, CAD-Dateien und Konformitätsleitfäden",
    fr: "Fiches techniques d'ingénierie, fichiers CAD et guides de conformité",
    pt: "Fichas técnicas de engenharia, arquivos CAD e guias de conformidade"
  },
  lead: {
    en: "Download verified technical documentation for stainless steel wire mesh specifications, aperture tables, and chemical resistance ratings.",
    zh: "下载关于不锈钢丝网规格、孔径对照表及耐化学腐蚀等级的经认证技术文档。",
    es: "Descargue documentación técnica verificada para especificaciones de malla de alambre de acero inoxidable, tablas de apertura y clasificaciones de resistencia química.",
    de: "Laden Sie geprüfte technische Dokumentationen zu Spezifikationen von Edelstahldrahtgewebe, Maschentabellen und chemischer Beständigkeit herunter.",
    fr: "Téléchargez la documentation technique vérifiée pour les spécifications de toiles métalliques en acier inoxydable, les tableaux d'ouverture et la résistance chimique.",
    pt: "Baixe documentação técnica verificada para especificações de malha de arame de aço inoxidável, tabelas de abertura e resistência química."
  },
  relatedBtn: {
    en: "Product datasheets",
    zh: "产品技术数据表",
    es: "Fichas de productos",
    de: "Produktdatenblätter",
    fr: "Fiches techniques produits",
    pt: "Fichas de produtos"
  },
  imageOverlay: {
    en: "Technical documentation library",
    zh: "技术文档与工程图纸库",
    es: "Biblioteca de documentación técnica",
    de: "Bibliothek technischer Dokumente",
    fr: "Bibliothèque de documentation technique",
    pt: "Biblioteca de documentação técnica"
  },
  imageAlt: {
    en: "Stainless steel woven wire mesh specification catalog and technical engineering drawings",
    zh: "不锈钢编织丝网技术规格目录与工程图纸",
    es: "Catálogo de especificaciones de malla de alambre tejida de acero inoxidable y planos técnicos",
    de: "Edelstahldrahtgewebe-Spezifikationskatalog und technische Zeichnungen",
    fr: "Catalogue de spécifications de toiles tissées en acier inoxydable et plans techniques",
    pt: "Catálogo de especificações de malha tecida de aço inoxidável e desenhos técnicos"
  },
  cardNote: {
    en: "PDF datasheets, CAD models, and alloy matrices",
    zh: "PDF 数据表、CAD 模型与合金对照矩阵",
    es: "Fichas en PDF, modelos CAD y matrices de aleación",
    de: "PDF-Datenblätter, CAD-Modelle und Legierungsmatrizen",
    fr: "Fiches PDF, modèles CAD et matrices d'alliages",
    pt: "Fichas em PDF, modelos CAD e matrizes de liga"
  },
  cardLink: {
    en: "View datasheets",
    zh: "查看数据表",
    es: "Ver fichas",
    de: "Datenblätter anzeigen",
    fr: "Voir les fiches",
    pt: "Ver fichas"
  }
};
const specStripData: Array<{
  label: Record<Lang, string>;
  value: string;
}> = [{
  label: {
    en: "Woven Mesh Range",
    zh: "编织丝网孔径范围",
    es: "Rango de malla tejida",
    de: "Gewebe-Bereich",
    fr: "Plage de toile tissée",
    pt: "Faixa de malha tecida"
  },
  value: "1 µm – 25 mm"
}, {
  label: {
    en: "Square Weave Mesh Counts",
    zh: "方孔编织目数范围",
    es: "Mallas de tejido cuadrado",
    de: "Quadratmaschen-Zahlen",
    fr: "Nombre de mailles carrées",
    pt: "Contagens de malha quadrada"
  },
  value: "1 – 635 mesh"
}, {
  label: {
    en: "Sintered Porous Range",
    zh: "烧结多孔过滤精度",
    es: "Rango sinterizado poroso",
    de: "Sinter-Porenbereich",
    fr: "Plage poreuse frittée",
    pt: "Faixa sinterizada porosa"
  },
  value: "1 – 200 µm"
}, {
  label: {
    en: "Wedge Wire Slot Width",
    zh: "楔形丝缝隙公差",
    es: "Abertura de alambre en cuña",
    de: "Spaltsieb-Spaltweite",
    fr: "Largeur de fente fils profilés",
    pt: "Abertura de arame em cunha"
  },
  value: "0.02 – 5.0 mm"
}];
export default function DownloadsHero() {
  const {
    lang
  } = useLanguage();
  return <section id="downloadshero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges Row - EXACTLY two outline badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                {pick(lang, heroCopy.categoryBadge)}
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                {pick(lang, heroCopy.standardBadge)}
              </Badge>
            </div>

            {/* Single Page Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {pick(lang, heroCopy.h1)}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {pick(lang, heroCopy.lead)}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="font-semibold transition-colors">
                <Link to="/DatasheetsPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{pick(lang, heroCopy.relatedBtn)}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Card (5 Columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media mf-ratio-hero relative w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/documentary-style-photograph-of-rolls-of-400d9e.png" alt={pick(lang, heroCopy.imageAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, heroCopy.imageOverlay)}</span>
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{pick(lang, heroCopy.cardNote)}</span>
                  </div>
                  <Link to="/DatasheetsPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{pick(lang, heroCopy.cardLink)}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specStripData.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{pick(lang, spec.label)}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}